import { useState } from "react";
import { toast } from "sonner";
import { X, Loader2, CreditCard, ShieldCheck, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { trackInitiateCheckout } from "@/lib/analytics";

/**
 * Details-first subscription dialog.
 *
 * Props:
 *   - open (bool)
 *   - onClose ()
 *   - packageId ("monitoring_month")
 *   - label (display name shown in title)
 *   - priceLabel ("$45/mo")
 *   - priceUsd (numeric value fired to Meta Pixel InitiateCheckout)
 */
export default function SubscribeDialog({
  open,
  onClose,
  packageId,
  label,
  priceLabel,
  priceUsd,
}) {
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    email: "",
    phone: "",
    profile: "",
  });
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e?.preventDefault?.();
    const errors = [];
    if (!form.name.trim()) errors.push("Full name");
    if (!form.business_name.trim()) errors.push("Business name");
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errors.push("A valid email");
    if (errors.length) {
      toast.error(`Please fill in: ${errors.join(", ")}`);
      return;
    }
    setLoading(true);
    try {
      trackInitiateCheckout(packageId, priceUsd);
      const { data } = await api.post("/payments/subscribe", {
        package_id: packageId,
        origin_url: window.location.origin,
        name: form.name.trim(),
        business_name: form.business_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || undefined,
        profile: form.profile.trim() || undefined,
      });
      window.location.href = data.url;
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Couldn't start your checkout — please try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Checkout failed.");
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/75 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && !loading && onClose()}
      data-testid="subscribe-dialog"
    >
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0d1424] p-6 sm:p-8 relative">
        {!loading && (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
            aria-label="Close"
            data-testid="subscribe-dialog-close"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#10B981] font-bold">
          <ShieldCheck className="w-3.5 h-3.5" />
          Subscribe · {priceLabel}
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold mt-2 leading-tight">
          Start your {label}
        </h2>
        <p className="mt-2 text-white/55 text-sm leading-relaxed">
          Tell us a little about your business — we'll email your welcome kit
          and take you to secure Stripe checkout.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <Field label="Your full name" required>
            <input
              type="text"
              value={form.name}
              onChange={set("name")}
              autoComplete="name"
              placeholder="Jane Rousseau"
              data-testid="subscribe-name"
              disabled={loading}
              className="w-full h-11 px-3.5 rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm placeholder:text-white/30 focus:border-[#10B981]/60 outline-none transition-colors"
            />
          </Field>
          <Field label="Business name" required>
            <input
              type="text"
              value={form.business_name}
              onChange={set("business_name")}
              autoComplete="organization"
              placeholder="Riverside Bistro"
              data-testid="subscribe-business"
              disabled={loading}
              className="w-full h-11 px-3.5 rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm placeholder:text-white/30 focus:border-[#10B981]/60 outline-none transition-colors"
            />
          </Field>
          <Field label="Email for receipts & alerts" required>
            <input
              type="email"
              value={form.email}
              onChange={set("email")}
              autoComplete="email"
              placeholder="you@business.com"
              data-testid="subscribe-email"
              disabled={loading}
              className="w-full h-11 px-3.5 rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm placeholder:text-white/30 focus:border-[#10B981]/60 outline-none transition-colors"
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Phone (optional)">
              <input
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                autoComplete="tel"
                placeholder="+92 333 000 0000"
                data-testid="subscribe-phone"
                disabled={loading}
                className="w-full h-11 px-3.5 rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm placeholder:text-white/30 focus:border-[#10B981]/60 outline-none transition-colors"
              />
            </Field>
            <Field label="Google Business URL (optional)">
              <input
                type="url"
                value={form.profile}
                onChange={set("profile")}
                placeholder="https://maps.google.com/…"
                data-testid="subscribe-profile"
                disabled={loading}
                className="w-full h-11 px-3.5 rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm placeholder:text-white/30 focus:border-[#10B981]/60 outline-none transition-colors"
              />
            </Field>
          </div>

          <div className="pt-3 flex flex-col-reverse sm:flex-row sm:justify-end sm:items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="text-white/55 hover:text-white text-sm font-semibold h-11 px-4 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="subscribe-submit"
              className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-[#10B981] hover:bg-[#0ea774] text-white font-semibold text-sm shadow-lg shadow-emerald-500/25 transition-colors disabled:opacity-70"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Redirecting to Stripe…
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  Continue to secure checkout
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </div>

          <p className="text-[11px] text-white/40 leading-relaxed pt-1">
            Cancel anytime from your dashboard. Payments processed by Stripe —
            your card details never touch our servers.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-semibold mb-1.5">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </div>
      {children}
    </label>
  );
}
